import { NEW_RELIC_SCRIPTS } from './newRelic'
import * as LOG_ROCKET from './logRocket'
import {
  addBreadcrumb,
  sentryClient,
  SentryErrorBoundary,
  clearUser as clearSentryUser,
  setUser as setSentryUser,
} from './sentry'

export { sentryClient, SentryErrorBoundary }

let USER_INFO = null
let USER_ID = null
let USER_IS_PRODUCTION = false
const USER_ID_STORAGE_KEY = 'residesk:logrocket:userId'
const ACTOR_CLASSES = new Set([
  'automation',
  'shared_operator',
  'demo',
  'internal_staff',
  'client_account',
  'unknown',
])

const fixedEventContext = () =>
  USER_INFO
    ? {
        userId: USER_ID,
        ...(Number.isSafeInteger(USER_INFO.accountId) && USER_INFO.accountId > 0
          ? { accountId: USER_INFO.accountId }
          : {}),
        actorClass: ACTOR_CLASSES.has(USER_INFO.actorClass) ? USER_INFO.actorClass : 'unknown',
      }
    : undefined

const readStoredUserId = () => {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage.getItem(USER_ID_STORAGE_KEY)
  } catch {
    return null
  }
}

const storeUserId = (id) => {
  try {
    if (typeof window === 'undefined') return
    if (id === null) window.sessionStorage.removeItem(USER_ID_STORAGE_KEY)
    else window.sessionStorage.setItem(USER_ID_STORAGE_KEY, id)
  } catch {
    // Session storage can be disabled; module-local tracking still works.
  }
}

export const TRACKERS = Object.freeze({
  LOG_ROCKET: {
    INIT: LOG_ROCKET.init,
    IDENTIFY: LOG_ROCKET.identify,
    TRACK: (label) => LOG_ROCKET.track(label, fixedEventContext()),
  },
  NEW_RELIC: {
    SCRIPT: import.meta.env.MODE !== 'development' ? NEW_RELIC_SCRIPTS.PRODUCTION : '',
  },
  SENTRY: {
    IDENTIFY(id, userInfo) {
      setSentryUser(id, userInfo)
    },
    TRACK: (type, metadata) => addBreadcrumb(type, metadata),
  },
})

/**
 * Identify the user across all of our tracking tools
 * @param {string} id
 * @param {{ name?:string, email?:string, isAdmin?:boolean, accountId?:string, accountName?:string, ... }} userInfo
 * @return
 */
export function identifyUser(id, userInfo = {}, isProduction = false) {
  if (!Number.isSafeInteger(id) || id <= 0) {
    console.error('Trying to identify user without ID')
    resetTrackingUser(isProduction)
    return
  }
  const nextId = String(id)
  const previousId = isProduction ? (USER_IS_PRODUCTION ? USER_ID : readStoredUserId()) : null
  if (isProduction && previousId && previousId !== nextId) {
    try {
      LOG_ROCKET.startNewSession()
    } catch (error) {
      console.error('Unable to separate LogRocket sessions:', error)
    }
  }
  USER_ID = nextId
  USER_IS_PRODUCTION = isProduction
  USER_INFO = userInfo
  if (isProduction) storeUserId(nextId)
  Object.values(TRACKERS).forEach((tracker) => {
    if (tracker?.initEverywhere || isProduction) {
      try {
        tracker.IDENTIFY?.(id, userInfo)
      } catch (error) {
        console.error('Unable to identify user for tracking:', error)
      }
    }
  })
}

export function resetTrackingUser(isProduction = USER_IS_PRODUCTION) {
  const previousId = USER_ID || (isProduction ? readStoredUserId() : null)
  const splitSession = isProduction && previousId !== null
  USER_INFO = null
  USER_ID = null
  USER_IS_PRODUCTION = false
  if (isProduction) storeUserId(null)
  if (splitSession) {
    try {
      LOG_ROCKET.startNewSession()
    } catch (error) {
      console.error('Unable to reset LogRocket session:', error)
    }
  }
  try {
    clearSentryUser()
  } catch (error) {
    console.error('Unable to clear Sentry user:', error)
  }
}

export function initializeScripts({ isProduction }) {
  Object.values(TRACKERS).forEach((tracker) => {
    if (tracker?.initEverywhere || isProduction) {
      tracker?.INIT?.({
        isProduction,
      })
    }
  })
}

/**
 * Identify the user across all of our tracking tools
 * @param {{ type?:string, ...}} action
 * @return
 */
export function trackActions(action = {}) {
  const { type, ...metaData } = action
  if (!type) {
    console.error('Trying to log action without type')
    return
  }
  Object.values(TRACKERS).forEach((tracker) => {
    if (!tracker?.TRACK) return
    try {
      tracker.TRACK(type, {
        ...metaData,
        ...(USER_INFO ? { user: USER_INFO } : {}),
      })
    } catch (error) {
      console.error('Unable to track action:', error)
    }
  })
}

const RENEWAL_EVENTS = new Set([
  'RENEWALS_DATA_READY',
  'RENEWALS_SECTION_EXPOSED',
  'RENEWALS_SECTION_ATTENTION',
  'RENEWALS_EXPORT_START',
  'RENEWALS_EXPORT_SUCCESS',
  'RENEWALS_EXPORT_FAILURE',
  'RENEWALS_EXPORT_UNCONFIRMED',
])
const RENEWAL_SECTIONS = new Set(['funnel', 'attention', 'proof'])
const RENEWAL_REASONS = new Set(['submit', 'job', 'timeout', 'network', 'missing_job', 'abandoned'])
const renewalEventFields = (name, fields) => {
  const selected = {}
  if (['legacy', 'new'].includes(fields.mode)) selected.mode = fields.mode
  if (Number.isSafeInteger(fields.portfolioCount) && fields.portfolioCount >= 0) {
    selected.portfolioCount = fields.portfolioCount
  }
  if (name.startsWith('RENEWALS_SECTION_')) {
    if (!RENEWAL_SECTIONS.has(fields.sectionKey)) return null
    selected.sectionKey = fields.sectionKey
  }
  if (name === 'RENEWALS_SECTION_ATTENTION') {
    for (const key of ['visibleMs', 'supportedMs', 'quietMs']) {
      if (!Number.isFinite(fields[key]) || fields[key] < 0) return null
      selected[key] = Math.round(fields[key])
    }
    selected.estimateVersion = 1
    const quiet = selected.quietMs
    selected.quietBand =
      quiet === 0
        ? '0s'
        : quiet < 30000
        ? '<30s'
        : quiet < 120000
        ? '30s-2m'
        : quiet < 300000
        ? '2m-5m'
        : '5m+'
  }
  if (name.startsWith('RENEWALS_EXPORT_')) {
    if (fields.format !== 'xlsx' && fields.format !== 'pdf') return null
    selected.format = fields.format
    if (name === 'RENEWALS_EXPORT_FAILURE' || name === 'RENEWALS_EXPORT_UNCONFIRMED') {
      if (!RENEWAL_REASONS.has(fields.outcomeReason)) return null
      selected.outcomeReason = fields.outcomeReason
    }
  }
  return selected
}

export function captureRenewalActor(expectedUserId, expectedAccountId) {
  if (
    !USER_INFO ||
    !Number.isSafeInteger(expectedUserId) ||
    !Number.isSafeInteger(expectedAccountId) ||
    expectedAccountId <= 0 ||
    USER_ID !== String(expectedUserId) ||
    USER_INFO.accountId !== expectedAccountId ||
    !ACTOR_CLASSES.has(USER_INFO.actorClass)
  )
    return null
  return { userId: USER_ID, accountId: expectedAccountId, actorClass: USER_INFO.actorClass }
}

// This selected path does not change the generic trackActions metadata boundary.
export function trackRenewalEvent(name, actor, fields = {}) {
  const fixedContext = fixedEventContext()
  if (
    !RENEWAL_EVENTS.has(name) ||
    !actor ||
    !fixedContext?.accountId ||
    actor.userId !== USER_ID ||
    actor.accountId !== USER_INFO?.accountId ||
    actor.actorClass !== USER_INFO?.actorClass ||
    !ACTOR_CLASSES.has(actor.actorClass)
  )
    return

  const selected = renewalEventFields(name, fields)
  if (!selected) return

  const context = { ...fixedContext, dashboardKey: 'renewals', ...selected }
  try {
    LOG_ROCKET.track(name, context)
  } catch (error) {
    console.error('Unable to track renewal event:', error)
  }
}

export const SCRIPTS = Object.values(TRACKERS).map((tracker) => tracker?.SCRIPT || '')
